import {AVATAR_ALLOWED_MIME_TYPES} from '@sollapay/utils';
import {useCallback, useImperativeHandle, useRef} from 'react';

import {useFileDragDrop} from '../../hooks/useFileDragDrop';
import {cn} from '../../lib/utils';
import {Avatar} from '../Avatar/Avatar';
import {Icon} from '../Icon/Icon';
import {IconButton} from '../IconButton/IconButton';
import {Spinner} from '../shadcn/spinner';

import type {AvatarShape} from '../Avatar/avatar-variants';
import type {Ref} from 'react';

const AVATAR_ACCEPT = AVATAR_ALLOWED_MIME_TYPES.join(',');

export interface AvatarPickerHandle {
  open: () => void;
}

export interface AvatarPickerProps {
  ref?: Ref<AvatarPickerHandle>;
  src?: string;
  initials?: string;
  seed?: string;
  shape?: AvatarShape;
  isUploading?: boolean;
  disabled?: boolean;
  onFileSelect: (file: File) => void;
  uploadLabel: string;
  className?: string;
}

/**
 * Compact avatar editor: the avatar with a camera edit-badge and drop target, no side panel.
 * Shows a person placeholder when there's neither an image nor initials. `ref.open()` lets
 * an external trigger (e.g. a text link) open the same file picker.
 */
export function AvatarPicker({
  ref,
  src,
  initials = '',
  seed,
  shape = 'circle',
  isUploading = false,
  disabled = false,
  onFileSelect,
  uploadLabel,
  className
}: AvatarPickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const isInert = isUploading || disabled;
  const {isDragActive, dragProps} = useFileDragDrop({
    disabled: isInert,
    multiple: false,
    onFilesSelect: files => {
      const file = files[0];
      if (file) onFileSelect(file);
    }
  });

  const open = useCallback(() => {
    if (!isInert) inputRef.current?.click();
  }, [isInert]);

  useImperativeHandle(ref, () => ({open}), [open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileSelect(file);
    e.target.value = '';
  };

  const hasFallbackContent = Boolean(src || initials);

  return (
    <div
      data-slot="avatar-picker"
      {...dragProps}
      className={cn(
        'relative size-20 shrink-0 rounded-full',
        isDragActive && 'ring-2 ring-brand-primary',
        className
      )}
    >
      {hasFallbackContent ? (
        <Avatar src={src} initials={initials} seed={seed} shape={shape} size="2xl" />
      ) : (
        <div
          data-slot="avatar-picker-placeholder"
          className={cn(
            'flex size-20 items-center justify-center border border-border bg-background-secondary text-fg-tertiary',
            shape === 'circle' ? 'rounded-full' : 'rounded-xl'
          )}
        >
          <Icon name="user" className="size-8" />
        </div>
      )}

      {isUploading && (
        <div
          data-slot="avatar-picker-uploading"
          className="absolute inset-0 flex items-center justify-center rounded-full bg-background/60"
        >
          <Spinner size="lg" />
        </div>
      )}

      <IconButton
        icon="camera"
        type="button"
        title={uploadLabel}
        aria-label={uploadLabel}
        disabled={isInert}
        onClick={open}
        className="absolute -bottom-0.5 -start-0.5 size-7"
      />

      <input
        ref={inputRef}
        type="file"
        accept={AVATAR_ACCEPT}
        disabled={isInert}
        className="hidden"
        onChange={handleChange}
      />
    </div>
  );
}
